import { jsx as _jsx } from "react/jsx-runtime";
import AtSymbolIcon from '@heroicons/react/outline/AtSymbolIcon';
import CalendarIcon from '@heroicons/react/outline/CalendarIcon';
import CheckCircleIcon from '@heroicons/react/outline/CheckCircleIcon';
import CheckIcon from '@heroicons/react/outline/CheckIcon';
import ClockIcon from '@heroicons/react/outline/ClockIcon';
import DocumentTextIcon from '@heroicons/react/outline/DocumentTextIcon';
import ExternalLinkIcon from '@heroicons/react/outline/ExternalLinkIcon';
import HashtagIcon from '@heroicons/react/outline/HashtagIcon';
import LinkIcon from '@heroicons/react/outline/LinkIcon';
import MenuAlt1Icon from '@heroicons/react/outline/MenuAlt1Icon';
import MenuAlt2Icon from '@heroicons/react/outline/MenuAlt2Icon';
import PaperClipIcon from '@heroicons/react/outline/PaperClipIcon';
import PhotographIcon from '@heroicons/react/outline/PhotographIcon';
import SelectorIcon from '@heroicons/react/outline/SelectorIcon';
import StarIcon from '@heroicons/react/outline/StarIcon';
import TagIcon from '@heroicons/react/outline/TagIcon';
import UserIcon from '@heroicons/react/outline/UserIcon';
import UsersIcon from '@heroicons/react/outline/UsersIcon';
import ViewGridIcon from '@heroicons/react/outline/ViewGridIcon';
import clsx from 'clsx';
import { CustomFieldPrivacyOptions, CustomFieldType, SpaceRoleType, } from '@tribeplatform/gql-client/types';
import { useAuthMember } from '@tribeplatform/react-sdk/hooks';
import { Image } from '../common';
export const TITLE_FIELD_KEY = 'title';
export const CONTENT_FIELD_KEY = 'content';
export const TRUNCATE_SCROLL_OFFSET = '-9rem';
export var CustomFieldSubtype;
(function (CustomFieldSubtype) {
    CustomFieldSubtype["CHECKBOX"] = "checkbox";
    CustomFieldSubtype["DATE"] = "date";
    CustomFieldSubtype["DATETIME"] = "datetime";
    CustomFieldSubtype["EMAIL"] = "email";
    CustomFieldSubtype["HTML"] = "html";
    CustomFieldSubtype["MULTISELECT"] = "multiselect";
    CustomFieldSubtype["NUMBER"] = "number";
    CustomFieldSubtype["SELECT"] = "select";
    CustomFieldSubtype["TEXT"] = "text";
    CustomFieldSubtype["TEXTAREA"] = "textarea";
    CustomFieldSubtype["TOGGLE"] = "toggle";
    CustomFieldSubtype["URL"] = "url";
    CustomFieldSubtype["RATING"] = "rating";
    CustomFieldSubtype["IMAGE"] = "image";
    CustomFieldSubtype["FILE"] = "file";
    CustomFieldSubtype["MEMBER"] = "member";
    CustomFieldSubtype["TAG"] = "tag";
    CustomFieldSubtype["SPACE"] = "space";
    CustomFieldSubtype["POST"] = "post";
    CustomFieldSubtype["IMAGES"] = "images";
    CustomFieldSubtype["FILES"] = "files";
    CustomFieldSubtype["MEMBERS"] = "members";
    CustomFieldSubtype["TAGS"] = "tags";
    CustomFieldSubtype["SPACES"] = "spaces";
    CustomFieldSubtype["POSTS"] = "posts";
})(CustomFieldSubtype || (CustomFieldSubtype = {}));
export var CustomFieldViews;
(function (CustomFieldViews) {
    CustomFieldViews["Text"] = "Text";
    CustomFieldViews["MultiLineText"] = "MultiLineText";
    CustomFieldViews["Title"] = "Title";
    CustomFieldViews["Button"] = "Button";
    CustomFieldViews["CommaSeparatedText"] = "CommaSeparatedText";
    CustomFieldViews["Email"] = "Email";
    CustomFieldViews["Phone"] = "Phone";
    CustomFieldViews["Url"] = "Url";
    CustomFieldViews["Html"] = "Html";
    CustomFieldViews["Number"] = "Number";
    CustomFieldViews["Rating"] = "Rating";
    CustomFieldViews["Date"] = "Date";
    CustomFieldViews["Check"] = "Check";
    CustomFieldViews["Pills"] = "Pills";
    CustomFieldViews["Pill"] = "Pill";
    CustomFieldViews["Location"] = "Location";
    CustomFieldViews["SocialMedia"] = "SocialMedia";
    CustomFieldViews["CoverImage"] = "CoverImage";
    CustomFieldViews["Gallery"] = "Gallery";
})(CustomFieldViews || (CustomFieldViews = {}));
export const getFieldSetting = (field, key) => {
    var _a, _b;
    const value = (_b = (_a = field === null || field === void 0 ? void 0 : field.settings) === null || _a === void 0 ? void 0 : _a.find(setting => setting.key === key)) === null || _b === void 0 ? void 0 : _b.value;
    if (!value) {
        return null;
    }
    try {
        return JSON.parse(value);
    }
    catch (err) {
        console.error('Cannot parse value in custom field');
        return null;
    }
};
const getNewSettings = (field, key, value) => {
    var _a, _b;
    const currentSettings = (_a = field === null || field === void 0 ? void 0 : field.settings) !== null && _a !== void 0 ? _a : [];
    const isNewKey = !((_b = field === null || field === void 0 ? void 0 : field.settings) === null || _b === void 0 ? void 0 : _b.some(setting => setting.key === key));
    if (isNewKey) {
        return [...currentSettings, { key, value }];
    }
    return currentSettings.map(setting => {
        if (setting.key === key) {
            return { key, value };
        }
        return setting;
    });
};
export const setFieldSetting = (field, key, value) => {
    const newValue = JSON.stringify(value);
    const newSettings = getNewSettings(field, key, newValue);
    return {
        ...field,
        settings: newSettings,
    };
};
export const getFieldIcon = field => {
    const fieldIcons = {
        [CustomFieldType.date]: _jsx(CalendarIcon, {}, void 0),
        [CustomFieldType.number]: _jsx(HashtagIcon, {}, void 0),
        [CustomFieldType.boolean]: _jsx(CheckCircleIcon, {}, void 0),
        [CustomFieldType.richText]: _jsx(DocumentTextIcon, {}, void 0),
        [CustomFieldType.relation]: _jsx(ExternalLinkIcon, {}, void 0),
        [CustomFieldSubtype.HTML]: _jsx(DocumentTextIcon, {}, void 0),
        [CustomFieldSubtype.TEXTAREA]: _jsx(MenuAlt1Icon, {}, void 0),
        [CustomFieldSubtype.URL]: _jsx(LinkIcon, {}, void 0),
        [CustomFieldSubtype.SELECT]: _jsx(SelectorIcon, {}, void 0),
        [CustomFieldSubtype.EMAIL]: _jsx(AtSymbolIcon, {}, void 0),
        [CustomFieldSubtype.CHECKBOX]: _jsx(CheckIcon, {}, void 0),
        [CustomFieldSubtype.TOGGLE]: _jsx(CheckCircleIcon, {}, void 0),
        [CustomFieldSubtype.DATETIME]: _jsx(ClockIcon, {}, void 0),
        [CustomFieldSubtype.RATING]: _jsx(StarIcon, {}, void 0),
        [CustomFieldSubtype.IMAGE]: _jsx(PhotographIcon, {}, void 0),
        [CustomFieldSubtype.IMAGES]: _jsx(PhotographIcon, {}, void 0),
        [CustomFieldSubtype.FILE]: _jsx(PaperClipIcon, {}, void 0),
        [CustomFieldSubtype.FILES]: _jsx(PaperClipIcon, {}, void 0),
        [CustomFieldSubtype.MEMBER]: _jsx(UserIcon, {}, void 0),
        [CustomFieldSubtype.MEMBERS]: _jsx(UsersIcon, {}, void 0),
        [CustomFieldSubtype.TAG]: _jsx(TagIcon, {}, void 0),
        [CustomFieldSubtype.TAGS]: _jsx(TagIcon, {}, void 0),
        [CustomFieldSubtype.POST]: _jsx(DocumentTextIcon, {}, void 0),
        [CustomFieldSubtype.POSTS]: _jsx(DocumentTextIcon, {}, void 0),
        [CustomFieldSubtype.SPACE]: _jsx(ViewGridIcon, {}, void 0),
        [CustomFieldSubtype.SPACES]: _jsx(ViewGridIcon, {}, void 0),
    };
    const icon = getFieldSetting(field, 'icon');
    if (icon) {
        return (_jsx(Image, { src: `/images/icons/${icon}.svg`, alt: field === null || field === void 0 ? void 0 : field.name, className: "w-full h-full" }, void 0));
    }
    const key = (field === null || field === void 0 ? void 0 : field.subtype) || getFieldSetting(field, 'subtype') || (field === null || field === void 0 ? void 0 : field.type);
    return fieldIcons[key] || _jsx(MenuAlt2Icon, {}, void 0);
};
export const useFieldPermissions = (space) => {
    var _a, _b, _c;
    const { isModerator, data: member } = useAuthMember();
    const isSpaceAdmin = ((_c = (_b = (_a = space === null || space === void 0 ? void 0 : space.authMemberProps) === null || _a === void 0 ? void 0 : _a.spaceMember) === null || _b === void 0 ? void 0 : _b.role) === null || _c === void 0 ? void 0 : _c.type) === SpaceRoleType.admin;
    return {
        canUpdateField: ({ field, entity, }) => {
            var _a, _b, _c, _d, _e;
            if (isSpaceAdmin || isModerator || !((_a = field === null || field === void 0 ? void 0 : field.writePrivacy) === null || _a === void 0 ? void 0 : _a.allow)) {
                return true;
            }
            const owner = ((_c = (_b = entity) === null || _b === void 0 ? void 0 : _b.owner) === null || _c === void 0 ? void 0 : _c.member) || entity;
            if (((_e = (_d = field === null || field === void 0 ? void 0 : field.writePrivacy) === null || _d === void 0 ? void 0 : _d.allow) === null || _e === void 0 ? void 0 : _e.indexOf(CustomFieldPrivacyOptions.OWN)) !==
                -1) {
                if ((owner === null || owner === void 0 ? void 0 : owner.id) === (member === null || member === void 0 ? void 0 : member.id) || !(entity === null || entity === void 0 ? void 0 : entity.id)) {
                    return true;
                }
            }
            return false;
        },
        canViewField: ({ field, entity, }) => {
            var _a, _b, _c, _d, _e;
            if (isSpaceAdmin || isModerator || !((_a = field === null || field === void 0 ? void 0 : field.readPrivacy) === null || _a === void 0 ? void 0 : _a.allow)) {
                return true;
            }
            const owner = ((_c = (_b = entity) === null || _b === void 0 ? void 0 : _b.owner) === null || _c === void 0 ? void 0 : _c.member) || entity;
            if (((_e = (_d = field === null || field === void 0 ? void 0 : field.readPrivacy) === null || _d === void 0 ? void 0 : _d.allow) === null || _e === void 0 ? void 0 : _e.indexOf(CustomFieldPrivacyOptions.OWN)) !== -1) {
                if ((owner === null || owner === void 0 ? void 0 : owner.id) === (member === null || member === void 0 ? void 0 : member.id) || !(entity === null || entity === void 0 ? void 0 : entity.id)) {
                    return true;
                }
            }
            return false;
        },
    };
};
export const getTitleClassName = (size) => {
    return clsx(size === 'sm' && 'text-lg font-semibold', size === 'md' && 'text-xl', size === 'lg' && 'text-2xl');
};
//# sourceMappingURL=utils.js.map